import { SearchParamNames } from '../../../models/enums';
import { SearchProductTileModel, SortByValues } from '../../../models/globals';
import { removeExtraWhitespace } from '../../../utilities/StringUtilities';
import { checkboxReducer } from './checkboxReducer';
import { SearchAction } from './SearchActions';

interface SearchState {
    courseCountMap: Record<string, number>;
    courseTypes: Set<string>;
    visibleProductTiles: SearchProductTileModel[];
    isLoading: boolean;
    page: number;
    pageSize: 25; // if increased, check GTM chunk size limits in analytics.publishSearchProductImpressionEvents
    productTiles: SearchProductTileModel[];
    searchTerms: string;
    subTopicMap: Record<string, { topic: string }>;
    subTopics: Set<string>;
    topicMap: Record<string, { subTopics: string[] }>;
    topics: Set<string>;
    sort: SortByValues | undefined;
    sortInUse: SortByValues;
}

// eslint-disable-next-line complexity
const searchReducer = (state: SearchState, action: SearchAction): SearchState => {
    switch (action.type) {
        case SearchParamNames.Query: {
            return {
                ...state,
                page: 1,
                searchTerms: action.payload,
            };
        }
        case 'queryInit': {
            return {
                ...state,
                courseCountMap: {},
                visibleProductTiles: [],
                isLoading: true,
                productTiles: [],
            };
        }
        case 'querySuccess': {
            const nextState = { ...state, isLoading: false, productTiles: action.payload };
            return sortAndCountTiles(nextState);
        }
        case 'queryFailure': {
            return { ...state, isLoading: false };
        }
        case SearchParamNames.CourseTypes:
        case SearchParamNames.Topics:
        case SearchParamNames.SubTopics: {
            return checkboxReducer({ ...state, page: 1 }, action);
        }
        case SearchParamNames.Page: {
            const nextState = { ...state, page: state.page + 1 };
            return sortAndCountTiles(nextState);
        }
        case SearchParamNames.Sort: {
            const nextState = { ...state, sort: action.payload };
            return state.sortInUse === action.payload ? nextState : sortAndCountTiles(nextState);
        }
        case 'reload': {
            const searchParams = new URLSearchParams(window.location.search);
            const searchTerms = removeExtraWhitespace(searchParams.get(SearchParamNames.Query));
            return {
                ...state,
                courseTypes: new Set(searchParams.getAll(SearchParamNames.CourseTypes)),
                page: parseInt(searchParams.get(SearchParamNames.Page) || '1', 0) || 1,
                searchTerms,
                subTopics: new Set(searchParams.getAll(SearchParamNames.SubTopics)),
                topics: new Set(searchParams.getAll(SearchParamNames.Topics)),
                sort: (searchParams.get(SearchParamNames.Sort) as SortByValues) || undefined,
            };
        }
        /* istanbul ignore next */
        default: {
            console.error(new Error(`Unrecognized action: ${JSON.stringify(action, null, 2)}`));

            return state;
        }
    }
};

const sortFnMap = {
    name: (a: SearchProductTileModel, b: SearchProductTileModel) => a.title.localeCompare(b.title),
    relevance: (a: SearchProductTileModel, b: SearchProductTileModel) => a.relevanceRank - b.relevanceRank,
};

const sortAndCountTiles = (state: SearchState): SearchState => {
    const { page, pageSize, productTiles, sort, searchTerms } = state;
    const newCourseCountMap: Record<string, number> = {};
    const newSortedTiles: SearchProductTileModel[] = [];
    const loadCount = page * pageSize;

    productTiles.forEach((product) => {
        newCourseCountMap[product.categoryCode] = newCourseCountMap[product.categoryCode] || 0;
        newCourseCountMap[product.categoryCode]++;
        newSortedTiles.push(product);
    });

    let newSortInUse: SearchState['sort'] = sort;
    if (!newSortInUse) {
        newSortInUse = searchTerms ? 'relevance' : 'name';
    }
    if (newSortInUse !== 'relevance') {
        newSortedTiles.sort(sortFnMap[newSortInUse]);
    }

    return {
        ...state,
        courseCountMap: newCourseCountMap,
        visibleProductTiles: newSortedTiles.slice(0, loadCount),
        sortInUse: newSortInUse,
    };
};

export { searchReducer, SearchState };
